import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    Checkbox,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Chip,
} from '@material-ui/core';
import { Delete, Add } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() =>
    createStyles({
        rowContainer: {
            display: 'flex',
            gap: 6,
            alignItems: 'flex-start',
            padding: '6px 0',
            borderBottom: '1px solid #f1f5f9',
            '&:last-child': { borderBottom: 'none' },
        },
        rowNum: {
            fontSize: '0.7rem',
            color: '#94a3b8',
            minWidth: 18,
            paddingTop: 10,
            textAlign: 'right',
        },
        headerLabel: {
            fontSize: '0.65rem',
            fontWeight: 600,
            color: '#94a3b8',
            textTransform: 'uppercase' as const,
            letterSpacing: 0.5,
        },
        errorRow: {
            backgroundColor: '#fef2f2',
            borderRadius: 4,
        },
        validCount: {
            fontSize: '0.75rem',
            color: '#64748b',
        },
    })
);

export interface BulkRow {
    title: string;
    amount: string;
    type: 'DEBIT' | 'CREDIT';
    category: string;
    tag: string;
    date: string;
    onCredit: boolean;
}

const emptyRow = (): BulkRow => ({
    title: '',
    amount: '',
    type: 'DEBIT',
    category: '',
    tag: '',
    date: '',
    onCredit: false,
});

const isRowEmpty = (r: BulkRow) => !r.title.trim() && !r.amount;
const isRowValid = (r: BulkRow) => r.title.trim() !== '' && r.amount !== '' && !isNaN(Number(r.amount)) && Number(r.amount) > 0;

interface BulkAddDialogProps {
    open: boolean;
    existingCategories: string[];
    existingTags: string[];
    onSave: (rows: BulkRow[]) => void;
    onClose: () => void;
}

export const BulkAddDialog: React.FC<BulkAddDialogProps> = ({
    open,
    existingCategories,
    existingTags,
    onSave,
    onClose,
}) => {
    const classes = useStyles();
    const INITIAL_ROWS = 5;
    const [rows, setRows] = useState<BulkRow[]>(() => Array.from({ length: INITIAL_ROWS }, emptyRow));
    const [submitted, setSubmitted] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            setRows(Array.from({ length: INITIAL_ROWS }, emptyRow));
            setSubmitted(false);
        }
    }, [open]);

    const updateRow = useCallback((idx: number, field: keyof BulkRow, value: string | boolean) => {
        setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    }, []);

    const deleteRow = useCallback((idx: number) => {
        setRows(prev => prev.length <= 1 ? [emptyRow()] : prev.filter((_, i) => i !== idx));
    }, []);

    const addRows = useCallback((count: number = 1) => {
        setRows(prev => [...prev, ...Array.from({ length: count }, emptyRow)]);
        setTimeout(() => contentRef.current?.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' }), 50);
    }, []);

    // Clipboard paste handler — parse TSV/CSV into rows
    const handlePaste = useCallback((e: React.ClipboardEvent, startIdx: number) => {
        const text = e.clipboardData.getData('text/plain');
        const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
        // Single cell (possibly with trailing newline from Excel) — let browser handle it
        if (lines.length <= 1 && !text.includes('\t')) return;
        e.preventDefault();
        const parsed: BulkRow[] = lines.map(line => {
            const cols = line.includes('\t') ? line.split('\t') : line.split(',');
            return {
                title: (cols[0] || '').trim(),
                amount: (cols[1] || '').trim().replace(/[$,]/g, ''),
                type: (cols[2] || '').trim().toUpperCase() === 'CREDIT' ? 'CREDIT' as const : 'DEBIT' as const,
                category: (cols[3] || '').trim(),
                tag: (cols[4] || '').trim(),
                date: (cols[5] || '').trim(),
                onCredit: false,
            };
        });

        setRows(prev => {
            const before = prev.slice(0, startIdx);
            const after = prev.slice(startIdx + parsed.length);
            return [...before, ...parsed, ...after];
        });
    }, []);

    const nonEmptyRows = rows.filter(r => !isRowEmpty(r));
    const validRows = nonEmptyRows.filter(isRowValid);
    const invalidRows = nonEmptyRows.filter(r => !isRowValid(r));

    const handleSave = () => {
        setSubmitted(true);
        if (validRows.length === 0) return;
        onSave(validRows);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    <Typography style={{ fontWeight: 700, fontSize: '1rem', flexGrow: 1 }}>Bulk Add Entries</Typography>
                    <Typography className={classes.validCount}>
                        {validRows.length} valid {validRows.length === 1 ? 'entry' : 'entries'}
                        {invalidRows.length > 0 && submitted && (
                            <span style={{ color: '#dc2626', marginLeft: 8 }}>
                                {invalidRows.length} incomplete
                            </span>
                        )}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent ref={contentRef} style={{ paddingBottom: 0 }}>
                {/* Paste hint */}
                <Typography style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 8 }}>
                    Tip: paste from a spreadsheet (columns: Title, Amount, Type, Category, Tag, Date)
                </Typography>

                {/* Column headers */}
                <Box style={{ display: 'flex', gap: 6, paddingBottom: 4, borderBottom: '2px solid #e2e8f0', alignItems: 'center' }}>
                    <Box style={{ minWidth: 18 }} />
                    <Box style={{ flex: 3 }}><Typography className={classes.headerLabel}>Title *</Typography></Box>
                    <Box style={{ flex: 1.5 }}><Typography className={classes.headerLabel}>Amount *</Typography></Box>
                    <Box style={{ flex: 1.2 }}><Typography className={classes.headerLabel}>Type</Typography></Box>
                    <Box style={{ flex: 2 }}><Typography className={classes.headerLabel}>Category</Typography></Box>
                    <Box style={{ flex: 2 }}><Typography className={classes.headerLabel}>Tag</Typography></Box>
                    <Box style={{ flex: 1.5 }}><Typography className={classes.headerLabel}>Date</Typography></Box>
                    <Box style={{ flex: 0.6, textAlign: 'center' }}><Typography className={classes.headerLabel}>Credit</Typography></Box>
                    <Box style={{ minWidth: 32 }} />
                </Box>

                {/* Rows */}
                {rows.map((row, idx) => {
                    const showError = submitted && !isRowEmpty(row) && !isRowValid(row);
                    return (
                        <Box
                            key={idx}
                            className={`${classes.rowContainer} ${showError ? classes.errorRow : ''}`}
                        >
                            <Typography className={classes.rowNum}>{idx + 1}</Typography>
                            <Box style={{ flex: 3 }}>
                                <TextField
                                    value={row.title}
                                    onChange={e => updateRow(idx, 'title', e.target.value)}
                                    onPaste={e => handlePaste(e, idx)}
                                    placeholder="Description"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    error={showError && !row.title.trim()}
                                    inputProps={{ style: { fontSize: '0.82rem', padding: '6px 8px' } }}
                                />
                            </Box>
                            <Box style={{ flex: 1.5 }}>
                                <TextField
                                    value={row.amount}
                                    onChange={e => updateRow(idx, 'amount', e.target.value)}
                                    placeholder="0.00"
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    error={showError && (!row.amount || isNaN(Number(row.amount)) || Number(row.amount) <= 0)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    inputProps={{ style: { fontSize: '0.82rem', padding: '6px 4px' }, step: '0.01', min: '0' }}
                                />
                            </Box>
                            <Box style={{ flex: 1.2 }}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <Select
                                        value={row.type}
                                        onChange={e => updateRow(idx, 'type', e.target.value as string)}
                                        style={{ fontSize: '0.82rem' }}
                                        inputProps={{ style: { padding: '6px 8px' } }}
                                    >
                                        <MenuItem value="DEBIT" style={{ fontSize: '0.82rem' }}>Debit</MenuItem>
                                        <MenuItem value="CREDIT" style={{ fontSize: '0.82rem' }}>Credit</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box style={{ flex: 2 }}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <Select
                                        value={row.category}
                                        onChange={e => updateRow(idx, 'category', e.target.value as string)}
                                        displayEmpty
                                        style={{ fontSize: '0.82rem' }}
                                        inputProps={{ style: { padding: '6px 8px' } }}
                                    >
                                        <MenuItem value="" style={{ fontSize: '0.82rem', color: '#94a3b8' }}><em>None</em></MenuItem>
                                        {existingCategories.map(c => (
                                            <MenuItem key={c} value={c} style={{ fontSize: '0.82rem' }}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box style={{ flex: 2 }}>
                                <TextField
                                    value={row.tag}
                                    onChange={e => updateRow(idx, 'tag', e.target.value)}
                                    placeholder="tag1, tag2"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    inputProps={{ style: { fontSize: '0.82rem', padding: '6px 8px' } }}
                                />
                            </Box>
                            <Box style={{ flex: 1.5 }}>
                                <TextField
                                    value={row.date}
                                    onChange={e => updateRow(idx, 'date', e.target.value)}
                                    type="date"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ style: { fontSize: '0.82rem', padding: '6px 8px' } }}
                                />
                            </Box>
                            <Box style={{ flex: 0.6, display: 'flex', justifyContent: 'center' }}>
                                <Checkbox
                                    checked={row.onCredit}
                                    onChange={e => updateRow(idx, 'onCredit', e.target.checked)}
                                    size="small"
                                    color="primary"
                                    style={{ padding: 2 }}
                                />
                            </Box>
                            <IconButton size="small" onClick={() => deleteRow(idx)} style={{ padding: 4, marginTop: 2 }}>
                                <Delete style={{ fontSize: '0.9rem', color: '#94a3b8' }} />
                            </IconButton>
                        </Box>
                    );
                })}

                {/* Add row buttons */}
                <Box style={{ padding: '8px 0', display: 'flex', gap: 8 }}>
                    <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() => addRows(1)}
                        style={{ fontSize: '0.75rem', textTransform: 'none', color: '#3b82f6' }}
                    >
                        Add row
                    </Button>
                    <Button
                        size="small"
                        onClick={() => addRows(5)}
                        style={{ fontSize: '0.75rem', textTransform: 'none', color: '#64748b' }}
                    >
                        +5 rows
                    </Button>
                </Box>

                {/* Category quick-fill chips */}
                {existingCategories.length > 0 && (
                    <Box style={{ padding: '4px 0 8px' }}>
                        <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: 4 }}>
                            Quick-fill category for empty rows:
                        </Typography>
                        <Box style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {existingCategories.slice(0, 10).map(c => (
                                <Chip
                                    key={c}
                                    label={c}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        setRows(prev => prev.map(r => r.category ? r : { ...r, category: c }));
                                    }}
                                    style={{ fontSize: '0.7rem', height: 22, cursor: 'pointer', color: '#475569', borderColor: '#cbd5e1' }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
                {/* Tag quick-fill chips */}
                {existingTags.length > 0 && (
                    <Box style={{ padding: '4px 0 8px' }}>
                        <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: 4 }}>
                            Quick-fill tag for empty rows:
                        </Typography>
                        <Box style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {existingTags.slice(0, 10).map(t => (
                                <Chip
                                    key={t}
                                    label={t}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        setRows(prev => prev.map(r => r.tag ? r : { ...r, tag: t }));
                                    }}
                                    style={{ fontSize: '0.7rem', height: 22, cursor: 'pointer', color: '#475569', borderColor: '#94a3b8' }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions style={{ padding: '8px 24px 16px' }}>
                <Button onClick={onClose} style={{ color: '#64748b' }}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={validRows.length === 0}
                    onClick={handleSave}
                >
                    Save {validRows.length > 0 ? `${validRows.length} ${validRows.length === 1 ? 'entry' : 'entries'}` : 'All'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
